/* eslint-disable max-lines */
'use client'

import clsx from 'clsx'
import { format } from 'date-fns'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ValueType } from 'recharts/types/component/DefaultTooltipContent'

import {
  CARTESIAN_GRID_STROKE,
  CHART_AXIS_STROKE,
  CHART_COLORS,
  CHART_TITLE_STYLES,
  DEFAULT_CHART_WIDTH,
  GRID_OPACITY,
  LANDING_CHART_COLORS,
} from '../../../constants/chart'
import { CHART_STYLES } from '../../../constants/dashboard'
import { TIME_SCALE_OPTIONS } from '../../../constants/formatting'
import { useWidth } from '../../../hooks/useWidth'
import { ChartType } from '../../../types/Dashboard/types'
import { getChartAnimationConfig } from '../../../utils/getChartAnimationConfig'
import { getChartGradients } from '../../../utils/getChartGradients'
import { ChartErrorLoadingIndicator } from '../../ChartErrorLoadingIndicator'
import ChartTooltip from '../ChartTooltip'
import { LineChartLegend } from '../LineChartLegend'
import { TimeScaleSelector } from '../TimeScaleSelector'

import { LandingPageChartTooltip } from './LandingPageChartTooltip'
import { ResponsiveLineChartDot } from './ResponsiveLineChartDot'
import { LineChartProps } from './types'
import { useResponsiveLineChartLogic } from './useResponsiveLineChartLogic'

export const ResponsiveLineChart: FC<LineChartProps> = ({
  animateWhenInView = false,
  axisYFormatter,
  chartResetKey,
  connectNulls = false,
  containerClassName,
  dashedKeys,
  data,
  dataKeys = ['val'],
  dot = false,
  entryPayloadFilter,
  error,
  extraStyles = '',
  header,
  height = 'h-1/2 md:h-full',
  hideLegend = false,
  hideYAxis = false,
  isStacked,
  labelFormatter,
  margin = { bottom: 18, left: 12, right: 34, top: 60 },
  max,
  min,
  setTimeScale,
  showGradientReferenceLine = false,
  showLandingTooltip = false,
  strokeColors,
  strokeWidth = 1,
  timeScale = 'All',
  timeScaleOptions = TIME_SCALE_OPTIONS,
  title,
  tooltipYFormatter,
  width = DEFAULT_CHART_WIDTH,
  xAxisOverrides,
  yTickCount,
}) => {
  const { setVisibility, visibility, xAxisFormat, xTooltipFormat } = useResponsiveLineChartLogic(
    data,
    dataKeys,
    timeScale,
  )

  const { ref: widthRef, width: chartWidth } = useWidth()

  const ChartElement = useMemo(() => (isStacked ? AreaChart : LineChart), [isStacked])

  const getLegendKeys = useMemo(() => {
    if (!dashedKeys?.length) return dataKeys

    return dataKeys.filter(key => !key.startsWith('Estimated'))
  }, [dataKeys, dashedKeys])

  const handleVisibilityChange = (newVisibility: number[] | ((prev: number[]) => number[])) => {
    const updatedVisibility = typeof newVisibility === 'function' ? newVisibility(visibility) : newVisibility

    if (!dashedKeys?.length) {
      setVisibility(updatedVisibility)
      return
    }

    const finalVisibility = [...visibility]

    const baseKeyIndices = dataKeys.reduce(
      (acc, key, index) => {
        if (!key.startsWith('Estimated')) {
          acc[key] = index
        }

        return acc
      },
      {} as Record<string, number>,
    )

    getLegendKeys.forEach((baseKey, legendIndex) => {
      const originalIndex = baseKeyIndices[baseKey]
      const estimatedKey = `Estimated ${baseKey}`
      const estimatedIndex = dataKeys.indexOf(estimatedKey)

      finalVisibility[originalIndex] = updatedVisibility[legendIndex]

      if (estimatedIndex !== -1) {
        finalVisibility[estimatedIndex] = updatedVisibility[legendIndex]
      }
    })

    setVisibility(finalVisibility)
  }

  const [isInView, setIsInView] = useState(false)
  const animationRef = useRef(null)

  useEffect(() => {
    if (!animateWhenInView) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
        }
      },
      { threshold: 0.1 },
    )

    if (animationRef.current) {
      observer.observe(animationRef.current)
    }

    return () => {
      if (animationRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer.unobserve(animationRef.current)
      }
    }
  }, [animateWhenInView])

  return (
    <div ref={widthRef} className={clsx('relative', height, width, containerClassName)}>
      <div ref={animateWhenInView ? animationRef : undefined} className="h-full w-full">
        {header}
        {!header && (
          <TimeScaleSelector
            className={clsx('absolute z-10', !chartWidth || chartWidth > 420 ? 'right-6 top-[21px]' : 'right-0 top-0')}
            setTimeScale={setTimeScale}
            timeScale={timeScale}
            timeScaleOptions={timeScaleOptions}
          />
        )}
        <ChartErrorLoadingIndicator error={error} data={data} />
        <ResponsiveContainer
          height="100%"
          className={clsx(CHART_STYLES, extraStyles)}
          key={animateWhenInView ? `${chartResetKey}-${timeScale}` : ''}
        >
          <ChartElement data={data} margin={margin}>
            {!header && <text {...CHART_TITLE_STYLES}>{title}</text>}
            {Number(data?.length) > 0 && dataKeys && dataKeys.length > 0 && (
              <>
                {showGradientReferenceLine && (
                  <>
                    <defs>
                      <pattern id="referenceLinePattern" width="2" height="100%" patternUnits="userSpaceOnUse">
                        <linearGradient id="fadeGradient" x1="0" y1="0" x2="0" y2="0.95">
                          <stop offset="0%" stopColor="#595a5c" stopOpacity="0" />
                          <stop offset="30%" stopColor="#595a5c" stopOpacity="0.6" />
                          <stop offset="50%" stopColor="#595a5c" stopOpacity="1" />
                          <stop offset="70%" stopColor="#595a5c" stopOpacity="0.6" />
                          <stop offset="100%" stopColor="#595a5c" stopOpacity="0" />
                        </linearGradient>
                        <rect width="2" height="100%" fill="url(#fadeGradient)" />
                      </pattern>
                    </defs>
                    <defs>
                      <linearGradient id="susdeLineGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor={LANDING_CHART_COLORS.sUSDe} stopOpacity="0" />
                        <stop offset="40%" stopColor={LANDING_CHART_COLORS.sUSDe} stopOpacity="1" />
                      </linearGradient>
                      <linearGradient id="estimatedSusdeLineGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor={LANDING_CHART_COLORS.sUSDe} stopOpacity="1" />
                        <stop offset="99%" stopColor={LANDING_CHART_COLORS.sUSDe} stopOpacity="0" />
                      </linearGradient>

                      <linearGradient id="treasuriesLineGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor={LANDING_CHART_COLORS.Treasuries} stopOpacity="0" />
                        <stop offset="44%" stopColor={LANDING_CHART_COLORS.Treasuries} stopOpacity="1" />
                      </linearGradient>
                      <linearGradient id="estimatedTreasuriesLineGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor={LANDING_CHART_COLORS.Treasuries} stopOpacity="1" />
                        <stop offset="99%" stopColor={LANDING_CHART_COLORS.Treasuries} stopOpacity="0" />
                      </linearGradient>

                      <linearGradient
                        id="usdtUsdcLineGradient"
                        x1="0"
                        y1="0"
                        x2="1700"
                        y2="0"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0%" stopColor={LANDING_CHART_COLORS['USDT/USDC']} stopOpacity="0" />
                        <stop offset="40%" stopColor={LANDING_CHART_COLORS['USDT/USDC']} stopOpacity="1" />
                      </linearGradient>
                      <linearGradient
                        id="estimatedUsdtUsdcLineGradient"
                        x1="0"
                        y1="0"
                        x2="1500"
                        y2="0"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0%" stopColor={LANDING_CHART_COLORS['USDT/USDC']} stopOpacity="1" />
                        <stop offset="99%" stopColor={LANDING_CHART_COLORS['USDT/USDC']} stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </>
                )}
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={CARTESIAN_GRID_STROKE}
                  opacity={GRID_OPACITY}
                  vertical={false}
                  horizOriginX={undefined}
                />
                <XAxis
                  dataKey="date"
                  stroke={CHART_AXIS_STROKE}
                  minTickGap={40}
                  tickFormatter={(value: number | Date) => format(value, xAxisFormat)}
                  fontSize={12}
                  type={'number'}
                  domain={[new Date().getTime(), 'dataMax']}
                  scale={'time'}
                  tickLine={false}
                  tickMargin={4}
                  padding={{ left: 0, right: 4 }}
                  {...xAxisOverrides}
                />
                <YAxis
                  stroke={CHART_AXIS_STROKE}
                  tickLine={false}
                  tickCount={yTickCount}
                  tickMargin={4}
                  domain={[min ?? 'auto', max ?? 'auto']}
                  tickFormatter={axisYFormatter}
                  fontSize={12}
                  hide={hideYAxis}
                />
                <Tooltip
                  wrapperStyle={{ border: 'none', outline: 'none' }}
                  formatter={(value: ValueType | [ValueType, number], i: number) =>
                    tooltipYFormatter ? tooltipYFormatter(value, i) : value
                  }
                  cursor={
                    showGradientReferenceLine
                      ? {
                          stroke: 'url(#referenceLinePattern)',
                          strokeWidth: 2,
                        }
                      : undefined
                  }
                  content={
                    showLandingTooltip ? (
                      <LandingPageChartTooltip
                        payload={data}
                        entryPayloadFilter={entryPayloadFilter}
                        dateFormatter={xAxisOverrides?.tickFormatter}
                      />
                    ) : (
                      <ChartTooltip
                        chartType={ChartType.LINE}
                        labelFormatter={
                          labelFormatter ??
                          ((value: string | number | Date) =>
                            typeof value === 'string' ? value : format(value, xTooltipFormat))
                        }
                        formatter={tooltipYFormatter}
                        allLabels={dataKeys}
                        labelColors={strokeColors}
                        entryPayloadFilter={entryPayloadFilter}
                        visibility={visibility}
                      />
                    )
                  }
                  cursorStyle={showGradientReferenceLine ? undefined : { display: 'none' }}
                  separator=": "
                />
                {dataKeys.map((dataKey, i) => {
                  const isDashed = dashedKeys?.includes(dataKey)

                  const { lineDot, lineStroke } = getChartGradients(dataKey, i, showGradientReferenceLine, strokeColors)

                  const animationProps = getChartAnimationConfig({
                    animateWhenInView,
                    chartResetKey,
                    isDashed,
                    isInView,
                  })

                  const getOpacity = () => {
                    if (animateWhenInView) {
                      return isInView ? visibility[i] : 0
                    }

                    return visibility[i]
                  }

                  return isStacked ? (
                    <Area
                      key={`${dataKey}-${chartResetKey}`}
                      stackId={1}
                      type="monotone"
                      dataKey={dataKey}
                      connectNulls={connectNulls}
                      stroke={strokeColors?.[dataKey] ?? CHART_COLORS[i % CHART_COLORS.length]}
                      opacity={getOpacity()}
                      fill={strokeColors?.[dataKey] ?? CHART_COLORS[i % CHART_COLORS.length]}
                      fillOpacity={0.1}
                      dot={dot}
                      strokeDasharray={isDashed ? '3 5' : undefined}
                      isAnimationActive={animateWhenInView ? visibility[i] !== 0 && isInView : visibility[i] !== 0}
                      activeDot={{ style: { display: visibility[i] === 0 ? 'none' : 'block' } }}
                      {...animationProps}
                    />
                  ) : (
                    <Line
                      key={`${dataKey}-${chartResetKey}`}
                      type="monotone"
                      dataKey={dataKey}
                      connectNulls={connectNulls}
                      stroke={lineStroke}
                      opacity={getOpacity()}
                      strokeWidth={strokeWidth}
                      dot={props => {
                        const { key, ...restProps } = props
                        return (
                          <ResponsiveLineChartDot
                            key={key}
                            {...restProps}
                            lineDot={lineDot}
                            data={data}
                            dataKey={dataKey}
                          />
                        )
                      }}
                      strokeDasharray={isDashed ? '3 5' : undefined}
                      isAnimationActive={animateWhenInView ? visibility[i] !== 0 && isInView : visibility[i] !== 0}
                      activeDot={{ style: { display: visibility[i] === 0 ? 'none' : 'block' } }}
                      {...animationProps}
                    />
                  )
                })}
                {!hideLegend && dataKeys.length > 1 && (
                  <Legend
                    align="center"
                    content={
                      <LineChartLegend
                        colors={strokeColors}
                        dataKeys={getLegendKeys}
                        visibility={visibility}
                        setVisibility={handleVisibilityChange}
                      />
                    }
                  />
                )}
              </>
            )}
            {data && data.length === 0 && (
              <text x="50%" y="53%" textAnchor="middle" fontSize={18} stroke="none" fill="white" fontWeight="normal">
                No data available
              </text>
            )}
          </ChartElement>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
